import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import { useToast } from './Toast';
import { getData } from '../../services/FetchNodeServices';
import html2pdf from "html2pdf.js";

export default function WecDetailsModal({ 
    isOpen, 
    onClose, 
    wec, 
    companySettings: propCompanySettings, 
    teamAndConditions: propTeamAndConditions 
}) {
    const { showToast } = useToast();
    const [companySettings, setCompanySettings] = useState(propCompanySettings || null);
    const [teamAndConditions, setTeamAndConditions] = useState(propTeamAndConditions || null);

    useEffect(() => {
        if (isOpen) {
            const fetchSettings = async () => {
                try {
                    if (!companySettings) {
                        const response = await getData('api/company/get-company-settings');
                        if (response?.status === true) {
                            setCompanySettings(response?.data);
                        }
                    }
                    if (!teamAndConditions) {
                        const response = await getData('api/company/get-AMC-settings');
                        if (response?.status === true) {
                            setTeamAndConditions(response?.data);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching settings inside WecDetailsModal:', error);
                }
            };
            fetchSettings();
        }
    }, [isOpen, companySettings, teamAndConditions]);

    // Keep state in sync with props if props change
    useEffect(() => {
        if (propCompanySettings) setCompanySettings(propCompanySettings);
    }, [propCompanySettings]);

    useEffect(() => {
        if (propTeamAndConditions) setTeamAndConditions(propTeamAndConditions);
    }, [propTeamAndConditions]);

    if (!wec) return null;

    const handleDownloadPdf = (record) => {
        const packageRows = record.PackageForms?.map((pkg, index) => {
            const selectedPkg = pkg.packageData?.packages?.find(
                (p) => String(p._id) === String(pkg.durationId)
            );

            return `
            <tr>
                <td>${index + 1}</td>
                <td>
                    ${record.productCategory || ''} - ${record.productBrand || ''}
                    ${record.productType ? `(${record.productType})` : ''}
                </td>
                <td>${record.productModel || ''}</td>
                <td>${record.serialNumber || 'N/A'}</td>
                <td>${pkg.packageData?.name || '—'}</td>
                <td>${selectedPkg?.validity || '—'}</td>
                <td>${pkg.startDate ? pkg.startDate.split('T')[0] : '—'}</td>
                <td>${pkg.endDate ? pkg.endDate.split('T')[0] : '—'}</td>
            </tr>
            `;
        }).join('');

        const template = `
        <div class="invoice-box">
            <div class="header">
                <div class="header-left">
                     <div class="logo">
                        <img src="${companySettings?.logo || ''}" alt="Company Logo" style="width:70px;height:70px;object-fit:contain;border-radius:8px;">
                    </div>
                    <div class="company-info">
                     <h2>${companySettings?.name || 'EMI PLUS CARE'}</h2>
                        <p>${companySettings?.address || 'C9/7 c-block diishad colony Delhi-95'}</p>
                        <p>${companySettings?.phone || '+91 8929391113'} | ${companySettings?.email || 'Support@emipluscare.in'}</p>
                    </div>
                </div>
                <div class="header-right">
                    <table class="meta-table">
                        <tr><td><strong>WEC No:</strong></td><td>${record?.id}</td></tr>
                        <tr><td><strong>Date:</strong></td><td>${new Date().toLocaleDateString('en-IN')}</td></tr>
                    </table>
                </div>
            </div>
    
            <div class="invoice-title">Warranty Extended Contract (WEC)</div>
    
            <table class="customer-table">
                <tr><th style="width: 30%;">Customer Name</th><td>${record.customerName}</td></tr>
                <tr><th>Address</th><td>${record.customerAddress}</td></tr>
                <tr><th>Contact No.</th><td>${record.customerMobile}</td></tr>
                <tr><th>Email</th><td>${record.customerEmail}</td></tr>
            </table>
    
            <table class="details-table">
                <thead>
                 <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>Model</th>
                        <th>Serial No.</th>
                        <th>Package Name</th>
                        <th>Validity</th>
                        <th>Valid From</th>
                        <th>Valid Till</th>
                    </tr>
                </thead>
                <tbody>
                    ${packageRows || '<tr><td colspan="8">No Packages Found</td></tr>'}
                </tbody>
            </table>
    
            <div class="summary">
                <table>
                    <tr><td>Subtotal</td><td>₹${record?.amcAmount}</td></tr>
                </table>
            </div>
    
            <div class="signature no-break">
                <div><strong>Note:</strong> Under the extended warranty, claims are limited to a maximum of 80% of the product's value (excluding GST). Please check the attachment for what is covered under our Terms & Conditions.</div>
            </div>

            <div class="signature no-break">
                <div>Thank you for choosing EMI PLUS CARE. For support, call us at +91 8929391113 or email us at support@emipluscare.in</div>
            </div>
    
                <div class="terms-content">
                    ${teamAndConditions?.termsAndConditions || 'No terms and conditions available.'}
                </div>
        </div>
        `;

        const container = document.createElement("div");
        container.innerHTML = `
        <html>
        <head>
            <style>
                @page {
                        margin: 15px;
                }
                
                body {
                    font-family: "Poppins", Arial, sans-serif;
                    background: #fff;
                    margin: 0;
                    padding: 15px;
                    font-size: 12px;
                    line-height: 1.4;
                }
                
                .invoice-box {
                    max-width: 800px;
                    margin: 0 auto;
                    background: #fff;
                    padding: 20px;
                    border: 1px solid #e0e0e0;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    border-radius: 6px;
                    box-sizing: border-box;
                }
                
                .header { 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: flex-start; 
                    border-bottom: 2px solid #007bff; 
                    padding-bottom: 15px;
                    margin-bottom: 20px;
                }
                
                .header-left {
                    display: flex;
                    align-items: flex-start;
                    gap: 15px;
                }
                
                .company-info h2 { 
                    margin: 0 0 5px 0; 
                    color: #007bff; 
                    font-size: 16px;
                }
                
                .company-info p {
                    margin: 2px 0;
                    font-size: 11px;
                }
                
                .meta-table {
                    width: auto;
                    min-width: 180px;
                    font-size: 11px;
                }
                
                .meta-table td {
                    padding: 4px 8px;
                    border: 1px solid #ddd;
                }
                
                .invoice-title { 
                    text-align: center; 
                    font-size: 16px; 
                    font-weight: 600; 
                    color: #222; 
                    margin: 20px 0; 
                }
                
                .customer-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 15px 0;
                    font-size: 11px;
                }
                
                .customer-table th, 
                .customer-table td { 
                    border: 1px solid #ddd; 
                    padding: 6px 8px; 
                    text-align: left;
                }
                
                .customer-table th { 
                    background: #007bff; 
                    color: #fff; 
                    font-weight: 600;
                }
                
                .details-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 15px 0;
                    font-size: 11px;
                }
                
                .details-table th, 
                .details-table td { 
                    border: 1px solid #ddd; 
                    padding: 6px 8px; 
                    text-align: left;
                    word-wrap: break-word;
                }
                
                .details-table th { 
                    background: #007bff; 
                    color: #fff; 
                    font-weight: 600;
                }
                
                .summary table {
                    width: auto;
                    margin-left: auto;
                    font-size: 11px;
                }
                
                .summary td {
                    border: 1px solid #ddd;
                    padding: 6px 12px;
                }
                
                .signature { 
                    margin: 15px 0;
                    padding: 8px;
                    font-size: 11px;
                    line-height: 1.3;
                }
                
                .terms-section {
                    margin-top: 25px;
                    page-break-before: always;
                }
                
                .terms-content {
                     page-break-before: always;
                        border: 1px solid #ccc;
                        padding: 15px;
                        border-radius: 6px;
                        margin-top: 30px;
                        font-size: 10px;
                        line-height: 1.2;
                        text-align: justify;
                }
                
                .terms-section strong {
                    font-size: 11px;
                }
                
                .no-break {
                    page-break-inside: avoid;
                }
                
                img {
                    max-width: 100%;
                    height: auto;
                }
            </style>
        </head>
        <body>${template}</body>
        </html>`;

        const opt = {
            margin: 0.5,
            filename: `WEC_${record.id}_${record.customerName.replace(/\s+/g, "_")}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                logging: false
            },
            jsPDF: {
                unit: "in",
                format: "a4",
                orientation: "portrait"
            },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        html2pdf().set(opt).from(container).save();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="WEC Details"
            size="xl"
        >
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Name</label>
                                <p className="text-gray-900">{wec.customerName}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Email</label>
                                <p className="text-gray-900">{wec.customerEmail}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Mobile</label>
                                <p className="text-gray-900">{wec.customerMobile}</p>
                            </div>
                            {wec.productPicture && (
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Product Picture</label>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                                            {wec.productPicture ? <img src={wec.productPicture} alt="Product Picture" className="w-full h-full object-cover rounded" /> : <i className="ri-file-line text-blue-600 w-4 h-4 flex items-center justify-center"></i>}
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => {
                                                try {
                                                    let downloadUrl = wec.productPicture;
                                                    if (downloadUrl.includes("/upload/")) {
                                                        downloadUrl = downloadUrl.replace("/upload/", "/upload/fl_attachment/");
                                                    }
                                                    const link = document.createElement("a");
                                                    link.href = downloadUrl;
                                                    link.download = wec.purchaseProof.split("/").pop() || "purchase-proof";
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                    showToast("File downloaded successfully", "success");
                                                } catch (error) {
                                                    console.error("Error downloading image:", error);
                                                    showToast("Failed to download file", "error");
                                                }
                                            }}
                                        >
                                            View / Download
                                        </Button>
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="text-sm font-medium text-gray-600">Address</label>
                                <p className="text-gray-900">{wec.customerAddress}</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Product</label>
                                <p className="text-gray-900">{wec.productCategory} - {wec.productBrand} {wec.productType}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Model</label>
                                <p className="text-gray-900">{wec.productModel}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Serial / IMEI Number</label>
                                <p className="text-gray-900">{wec.serialNumber || 'Not provided'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Purchase Value</label>
                                <p className="text-gray-900">₹{wec.purchaseValue?.toLocaleString()}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">WEC Amount ({wec.amcPercentage}%)</label>
                                <p className="text-gray-900 font-semibold">₹{wec.amcAmount?.toLocaleString()}</p>
                            </div>
                            {wec.purchaseProof && (
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Purchase Proof</label>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                                            {wec.purchaseProof ? <img src={wec.purchaseProof} alt="Purchase Proof" className="w-full h-full object-cover rounded" /> : <i className="ri-file-line text-blue-600 w-4 h-4 flex items-center justify-center"></i>}
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => {
                                                try {
                                                    let downloadUrl = wec.purchaseProof;
                                                    if (downloadUrl.includes("/upload/")) {
                                                        downloadUrl = downloadUrl.replace("/upload/", "/upload/fl_attachment/");
                                                    }
                                                    const link = document.createElement("a");
                                                    link.href = downloadUrl;
                                                    link.download = wec.purchaseProof.split("/").pop() || "purchase-proof";
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                    showToast("File downloaded successfully", "success");
                                                } catch (error) {
                                                    console.error("Error downloading image:", error);
                                                    showToast("Failed to download file", "error");
                                                }
                                            }}
                                        >
                                            View / Download
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Package Information
                    </h3>

                    <div className="space-y-4">
                        <div style={{ display: 'flex', gap: "40%" }}>
                            {wec.retailerName && (
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Retailer</label>
                                    <p className="text-gray-900">
                                        {wec.retailerName || 'N/A'}
                                    </p>
                                </div>
                            )}

                            {wec.createByPromoter && (
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Promoter</label>
                                    <p className="text-gray-900">
                                        {wec.createByPromoter.name || 'N/A'}
                                    </p>
                                </div>
                            )}
                        </div>
                        {wec.distributorName && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Distributor</label>
                                <p className="text-gray-900">{wec.distributorName}</p>
                            </div>
                        )}

                        {wec.PackageForms?.length > 0 ? (
                            wec.PackageForms.map((pkg, index) => {
                                const selectedPkg = pkg.packageData?.packages?.find(
                                    (p) => p._id === pkg.durationId
                                );

                                return (
                                    <div
                                        key={pkg._id || index}
                                        className="border rounded-lg p-4 bg-gray-50 space-y-2"
                                    >
                                        <div className="flex justify-between">
                                            <span className="text-sm font-medium text-gray-700">
                                                Package #{index + 1}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {pkg.packageData?.name}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                            <div style={{ display: 'flex' }}>
                                                <label className="text-gray-600">Validity :- </label>
                                                <p className="text-gray-900">
                                                    {selectedPkg?.validity || '—'}
                                                </p>
                                            </div>

                                            <div style={{ display: 'flex' }}>
                                                <label className="text-gray-600">Start Date :- </label>
                                                <p className="text-gray-900">
                                                    {pkg.startDate
                                                        ? new Date(pkg.startDate).toLocaleDateString('en-IN')
                                                        : '—'}
                                                </p>
                                            </div>

                                            <div style={{ display: 'flex' }}>
                                                <label className="text-gray-600">End Date :- </label>
                                                <p className="text-gray-900">
                                                    {pkg.endDate
                                                        ? new Date(pkg.endDate).toLocaleDateString('en-IN')
                                                        : '—'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="text-sm text-gray-500">No packages assigned</p>
                        )}
                    </div>
                </div>

                <div className="flex justify-end space-x-3">
                    <Button
                        variant="secondary"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => handleDownloadPdf(wec)}
                    >
                        <i className="ri-download-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                        Download PDF
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
